import useIntl from '@/utils/intl'
import React, { FunctionComponent, useEffect } from 'react'
import { ArrowForward } from '@mui/icons-material'
import { SpecialOffer } from '@/services/cms-api/home-page-content/types'
import Countdown from 'react-countdown'
import { useLanguage } from '@/context/LanguageContext'
import { Dialog, Slide } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import SpecialOffersContent from './specialOfferContent'
import NewsContent from './newsContent'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import Image from 'next/image'
import { IMG_URL, resolveImageUrl } from '@/utils/constants'

const Transition = React.forwardRef(function Transition (
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />
})

interface Category {
  title: string
  key: string
  id: string
  defaultIcon: string
  activeIcon: string
}
interface Prop {
  specialOffers: SpecialOffer[]
}
const SpecialOffers: FunctionComponent<Prop> = ({ specialOffers }) => {
  const { translate } = useIntl()
  const { locale } = useLanguage()
  const pathName = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()
  const contentWrapper = React.useRef(null)
  const [clicked, setClicked] = React.useState(0)
  const [open, setOpen] = React.useState(false)
  const [clickedItem, setClickedItem] = React.useState<SpecialOffer | undefined>()
  const [data, setData] = React.useState(specialOffers)
  const categories: Category[] = [
    {
      title: translate('all'),
      key: 'All',
      id: 'category.all',
      defaultIcon: 'icons/book-queue-default.svg',
      activeIcon: 'icons/book-queue.svg'
    },
    {
      title: translate('menu.promo'),
      key: 'Promotion',
      id: 'category.promotion',
      defaultIcon: 'icons/star-default.svg',
      activeIcon: 'icons/star.svg'
    },
    {
      title: translate('menu.registration'),
      key: 'Registration',
      id: 'category.registration',
      defaultIcon: 'icons/star-default.svg',
      activeIcon: 'icons/star.svg'
    },
    {
      title: translate('menu.news'),
      key: 'News',
      id: 'category.news',
      defaultIcon: 'icons/newspaper-default.svg',
      activeIcon: 'icons/newspaper.svg'
    },
    {
      title: translate('menu.notice'),
      key: 'Notice',
      id: 'category.notice',
      defaultIcon: 'icons/flag-default.svg',
      activeIcon: 'icons/flag.svg'
    },
    {
      title: translate('menu.advice'),
      key: 'Tips',
      id: 'category.advice',
      defaultIcon: 'icons/bulb-default.svg',
      activeIcon: 'icons/bulb.svg'
    }
  ]
  useEffect(() => {
    const id = searchParams.get('id')
    if (id !== null) {
      const item = specialOffers.find(e => e.id === parseInt(id))
      if (item !== undefined) {
        setClickedItem(item)
        setOpen(true)
      }
    } else {
      setClickedItem(undefined)
      setOpen(false)
    }
  }, [searchParams])

  const handleOpen = (item: SpecialOffer): void => {
    if (item.link !== undefined && item.link !== null) {
      router.push(item.link)
    } else {
      router.push(pathName + `?id=${item.id.toString()}`, { scroll:false })
    }
  }

  const handleClose = (): void => {
    router.push(pathName, { scroll:false })
    setOpen(false)
  }
  const handleCategoryChange = (i: number): void => {
    setClicked(i)
    if (i === 0) {
      setData(specialOffers)
    } else {
      const key = categories[i].key
      const result = specialOffers.filter((item) => item.type === key)
      setData(result)
    }
  }

  const sideScroll = (
    element: HTMLDivElement | null,
    speed: number,
    distance: number,
    step: number
  ): void => {
    if (element == null) return

    let scrollAmount = 0
    const slideTimer = setInterval(() => {
      element.scrollLeft += step
      scrollAmount += Math.abs(step)
      if (scrollAmount >= distance) {
        clearInterval(slideTimer)
      }
    }, speed)
  }

  if (specialOffers === undefined || specialOffers === null) {
    return <></>
  }

  return (
    <div id='specialOffers' className='h-[650px] lg:h-[800px] mt-6 relative flex flex-col items-center'>
      <div className='bg-secondaryAdditional rounded-xl h-5/6 flex flex-col items-center w-[95vw] bg-cover' style={{ backgroundImage: `url(${IMG_URL}/uploads/specialOfferBG.png)` }}>
        <p className='text-textAdditional font-extrabold xl:text-5xl md:text-4xl text-3xl mt-14 lg:mt-20'>{translate('home.special')}</p>
        <div className='flex items-center mt-8 lg:mt-16'>
          <ul className='border border-[#4E4E4E] bg-appBar2 flex items-center justify-center gap-1 p-2 rounded-2xl'>
            {categories.map((item: Category, i: number) => (
              <li
                id={item.id}
                key={i}
                className={'px-2 xl:min-w-40 md:min-w-30 md:min-w-24 lg:px-5 text-sm py-2 rounded-xl md:h-10 flex items-center font-semibold cursor-pointer md:hover:bg-secondaryAdditional hover:text-textAdditional text-center ' + (i === clicked ? 'bg-surfaceInverse text-textHigh' : 'text-medium text-textLow')}
                onClick={() => handleCategoryChange(i)}
              >
                <span className='md:flex hidden'>{item.title}</span>
                <Image draggable={false} width={30} height={30} alt='img' src={i === clicked ? item.activeIcon : item.defaultIcon} className='w-5 h-5 md:hidden' />
              </li>
            ))}
          </ul>
          <div id='news.more' className='items-center ml-4 text-base hidden lg:flex hover:underline cursor-pointer text-textInverse' onClick={() => router.push('/news')}>
            <p className='ml-4 font-semibold text-sm text-textAdditional'>{translate('shop.introduction')}</p>
            <ArrowForward className='lg:text-base text-sm text-textAdditional' />
          </div>
        </div>
      </div>
      <div className='w-full flex items-center justify-center'>
        <div ref={contentWrapper} className='absolute flex gap-6 top-40 lg:top-56 overflow-x-scroll no-scrollbar flex-nowrap w-full p-16 scroll-smooth' style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {data.map((item: SpecialOffer, i: number) => (
            <div id={'offer.' + item.id.toString()} key={i} className='group/item bg-cover rounded-xl max-w-none transition-all duration-500 cursor-pointer hover:scale-110' style={{ backgroundImage: `url(${resolveImageUrl(item.coverImage)})` }}>
              <div className='group/edit w-[235px] h-[353px] 2xl:w-[349px] 2xl:h-[509px] xl:w-[300px] xl:h-[418px] bg-gradient-to-t from-black px-5 rounded-xl flex flex-col justify-between p-4 group-hover/item:from-black group-hover/item:via-black/60 group-hover/item:to-black/70' onClick={() => handleOpen(item)}>
                <div className='flex justify-between'>
                  {
                    item.type === 'Promotion' || item.type === 'Registration'
                      ? (
                        <div className='group/edit px-4 py-2 gap-1 items-center justify-between bg-[#282828] rounded-full flex 2xl:flex-row flex-col h-fit invisible group-hover/item:visible 2xl:w-fit'>
                          <span className='text-xs text-[#8C8C8C] font-semibold flex'>{item.type === 'Promotion'
                            ? translate('expire')
                            : translate('registrationExpire')}:
                          </span>
                          {
                            item.expireDate !== undefined && (
                              <span className='text-xs lg:text-sm text-textInverse font-semibold'>
                                <Countdown date={item.expireDate} />
                              </span>
                            )
                          }
                        </div>
                        )
                      : <div />
                  }

                  <div>
                    <div className='flex p-2 items-center bg-[#282828] rounded-full h-fit justify-center w-fit'>
                      <Image draggable={false} width={30} height={30} alt='img' src={item.type === 'Promotion' ? resolveImageUrl('/uploads/icons/star.svg') : item.type === 'Registration' ? resolveImageUrl('/uploads/icons/star.svg') : item.type === 'News' ? resolveImageUrl('/uploads/icons/newspaper.svg') : resolveImageUrl('/uploads/icons/bulb.svg')} className='w-4 xl:w-5' />
                    </div>
                    <div className='group/edit flex justify-end w-full mt-2 invisible group-hover/item:visible'>
                      <div className='flex p-2 items-center bg-[#282828] rounded-full h-fit justify-center w-fit'>
                        <Image draggable={false} width={30} height={30} alt='img' src={resolveImageUrl('/uploads/icons/share.svg')} className='w-4 xl:w-5' />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='group/edit group-hover/item:-translate-y-2/3 xl:group-hover/item:-translate-y-20 2xl:group-hover/item:-translate-y-6 transition-all duration-500 mt-40'>
                  <span className='text-textAdditional font-bold 2xl:text-lg xl:text-base text-sm'>{locale === 'mn' ? item.titleMn : item.titleEn}</span>
                  <hr className='border-white/25 xl:my-4 my-3' />
                  <p className='text-[#B0B0B0] font-semibold hidden group/edit group-hover/item:flex xl:text-sm text-xs max-h-28 overflow-auto line-clamp-5'>{locale === 'mn' ? item.descriptionMn : item.descriptionEn}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className='hidden md:flex w-[95vw] gap-3 justify-end 2xl:translate-y-40 2xl:mt-0 xl:mt-20 lg:mt-8 md:mt-14'>
          <DarkIconButton type='darkoutlined' size='sm' iconImg={resolveImageUrl('/uploads/icons/chevron-left.svg')} onClick={() => sideScroll(contentWrapper.current, 50, 200, -window.innerWidth / 3)} />
          <DarkIconButton type='darkoutlined' size='sm' iconImg={resolveImageUrl('/uploads/icons/chevron-right.svg')} onClick={() => sideScroll(contentWrapper.current, 50, 200, window.innerWidth / 3)} />
        </div>

        {/* <div className='w-full flex justify-center md:justify-end mt-20 '>
          <div className='hidden md:flex border border-black p-2 w-10 h-10 rounded-full hover:bg-black hover:stroke-white mr-12 cursor-pointer'>
            <Image draggable={false} src={chevRight} alt='right' width={20} height={20} />
          </div>
          <div className='flex items-center ml-4 text-base md:hidden'>
            <p className='ml-4 font-medium'>{translate('all')}</p>
            <ArrowForward className='text-sm ml-2' />
          </div>
        </div> */}
      </div>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        className='bg-secondaryAdditional bg-cover backdrop-blur'
        style={{ backgroundImage: `url(${IMG_URL}/uploads/Ellipse.webp)` }}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none'
          }
        }}
      >
        {
          open && clickedItem !== undefined && clickedItem.content !== undefined
            ? (
                clickedItem.type === 'Promotion' || clickedItem.type === 'Registration'
                  ? <SpecialOffersContent
                      data={clickedItem}
                      onClose={handleClose}
                    />
                  : <NewsContent
                      data={clickedItem}
                      onClose={handleClose}
                    />
              )
            : <></>
        }
      </Dialog>
    </div>
  )
}
export default SpecialOffers
